/**
 * Combinações aleatórias de smilies (API xatblog).
 * Sempre via proxy local (`/api/v1/xat/...`) — Vite em DEV, Laravel em produção.
 */

export type RandomSmilieCombo = {
  id: number;
  code: string;
  powers: string[];
};

function randomSmilieEndpoint(): string {
  return '/api/v1/xat/smilies/random';
}

function listSmiliesEndpoint(): string {
  return '/api/v1/xat/smilies/list';
}

function pickCodeFromPayload(data: {
  code_str?: unknown;
  code?: unknown;
}): string {
  // random → code_str; list → code
  const fromStr = String(data.code_str ?? '').trim();
  if (fromStr) return fromStr;
  const fromCode = data.code;
  if (typeof fromCode === 'string') return fromCode.trim();
  if (typeof fromCode === 'number' && Number.isFinite(fromCode)) {
    // `code: 200` na envelope — ignorar
    return '';
  }
  return '';
}

export async function fetchRandomSmilieCombo(
  signal?: AbortSignal
): Promise<RandomSmilieCombo | null> {
  try {
    const res = await fetch(randomSmilieEndpoint(), {
      cache: 'no-store',
      headers: { Accept: 'application/json' },
      signal,
    });
    if (!res.ok) return null;
    const data = (await res.json()) as {
      code?: number | string;
      code_str?: string;
      id?: number;
      powers?: string[];
    };
    const code = pickCodeFromPayload(data);
    if (!code || !code.includes('#')) return null;
    return {
      id: typeof data.id === 'number' ? data.id : Date.now(),
      code,
      powers: Array.isArray(data.powers) ? data.powers.map(String) : [],
    };
  } catch {
    return null;
  }
}

/** Lista da comunidade — usada como fallback se /random falhar. */
async function fetchSmilieListCodes(signal?: AbortSignal): Promise<string[]> {
  try {
    const res = await fetch(listSmiliesEndpoint(), {
      cache: 'no-store',
      headers: { Accept: 'application/json' },
      signal,
    });
    if (!res.ok) return [];
    const data = (await res.json()) as {
      smilies?: Array<{ code?: string; code_str?: string }>;
    };
    if (!Array.isArray(data.smilies)) return [];
    const codes: string[] = [];
    for (const row of data.smilies) {
      const code = pickCodeFromPayload(row);
      if (code.includes('#')) codes.push(code);
    }
    return codes;
  } catch {
    return [];
  }
}

function shufflePick(codes: string[], count: number): string[] {
  if (codes.length === 0 || count <= 0) return [];
  const copy = [...codes];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  const picked: string[] = [];
  const seen = new Set<string>();
  for (const code of copy) {
    if (seen.has(code)) continue;
    seen.add(code);
    picked.push(code);
    if (picked.length >= count) break;
  }
  return picked;
}

/** Busca N combinações distintas para o hub (ex.: 5 smilies flutuantes). */
export async function fetchRandomSmilieCombos(
  count: number,
  signal?: AbortSignal
): Promise<string[]> {
  const target = Math.max(0, count);
  if (target === 0) return [];

  // Pede extras em paralelo para cobrir possíveis duplicados / falhas.
  const results = await Promise.all(
    Array.from({ length: target + 4 }, () => fetchRandomSmilieCombo(signal))
  );

  const codes: string[] = [];
  const seen = new Set<string>();
  for (const row of results) {
    const code = row?.code?.trim();
    if (!code || seen.has(code)) continue;
    seen.add(code);
    codes.push(code);
    if (codes.length >= target) break;
  }

  if (codes.length >= target) return codes;

  if (signal?.aborted) return codes;

  const fromList = await fetchSmilieListCodes(signal);
  for (const code of shufflePick(fromList, target * 2)) {
    if (seen.has(code)) continue;
    seen.add(code);
    codes.push(code);
    if (codes.length >= target) break;
  }

  return codes;
}
